import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import { BRANDS_TYPE } from 'utils/types';
import SalesHighlightsWidget from 'components/SalesHighlightsWidget';
import { Widget, SearchInput } from '@spins/react-ui-library';
import OverviewTable from 'components/OverviewTable';
import {
  ACCOUNT_POD_GAINS_SERIES_PORTFOLIO,
  ACCOUNT_POD_LOSSES_SERIES_PORTFOLIO,
  ACCOUNT_POD_OPPORTUNITIES_SERIES_PORTFOLIO,
} from 'apollo/queries';
import { ROUTES } from 'utils/routes';
import { periodEndWeekSelector } from 'containers/AccessControl/selectors';
import * as globalSelectors from 'store/global/selectors';
import { globalFilterWeeksSelector } from 'containers/PageHeader/selectors';
import * as pageHeaderActions from 'containers/PageHeader/actions';
import PortfolioPageHeaderContainer from 'containers/Portfolio/PageHeader';
import * as globalActions from 'store/global/actions';
import ReactGA from 'react-ga';
import DistributionChartWidget from 'components/Charts/Widgets/DistributionChartWidget';
import TrendChartWidget from 'components/Charts/Widgets/TrendChartWidget';
import ShareAccountChartWidget from 'components/Charts/Widgets/ShareAccountChartWidget';
import {
  useTableQueryState,
  useTableQuery,
  QueryResult,
  QueryStateResult,
} from '@spins/react-table';
import { BrandResumePortfolioDocument } from 'generated/signals-graphql';
import TableFilterTiles from 'components/Filter/TableFilterTiles';
import TableTabs from 'components/TableTabs';
import BrandsTable from 'components/Tables/Brands';

const Portfolio = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const clientId: string = useSelector(globalSelectors.clientIdSelector);
  const enterpriseBrand: string = useSelector(globalSelectors.enterpriseBrandSelector);
  const periodEndWeek = useSelector(periodEndWeekSelector);
  const weeks = useSelector(globalFilterWeeksSelector);

  const selectedTab = BRANDS_TYPE;
  const portfolioFilter = {
    weeks,
  };

  useEffect(() => {
    ReactGA.pageview(ROUTES.PORTFOLIO);
  }, []);

  const handleBrandSelect = (brand: string) => {
    dispatch(globalActions.setCurrentBrand(brand));
    dispatch(pageHeaderActions.updateGlobalFilterBrand(brand));
    history.push(ROUTES.OVERVIEW);
  };

  // tables state
  const tablesState: QueryStateResult = useTableQueryState([
    {
      id: 'brands',
      defaultSortAttribute: 'name',
      defaultSortDescending: false,
      queryVariables: {
        clientID: clientId,
        globalFilter: portfolioFilter,
      },
    },
  ]);

  // brands query
  const { data, loading, error }: QueryResult = useTableQuery(BrandResumePortfolioDocument, {
    fetchPolicy: 'cache-first',
    queryState: tablesState.queryState('brands'),
    searchAttributes: ['name'],
  });

  return (
    <React.Fragment>
      <Row>
        <PortfolioPageHeaderContainer />
      </Row>
      <Row className="mt-3">
        <Col>
          <SalesHighlightsWidget
            clientId={clientId}
            currentBrand={enterpriseBrand}
            periodEndWeek={periodEndWeek}
            globalFilter={portfolioFilter}
            type="portfolio"
          />
        </Col>
      </Row>
      <Row>
        <Col md={4} className="mt-3">
          <TrendChartWidget globalFilter={portfolioFilter} portfolio={true} />
        </Col>
        <Col md={4} className="mt-3">
          <ShareAccountChartWidget globalFilter={portfolioFilter} portfolio={true} />
        </Col>
        <Col md={4} className="mt-3">
          <DistributionChartWidget globalFilter={portfolioFilter} portfolio={true} />
        </Col>
      </Row>
      <Row className="mt-3">
        <Col>
          <Widget>
            {{
              header: {
                title: 'Top 10 Account Summary',
              },
              body: {
                content: (
                  <Row>
                    <Col lg={12} xl={4}>
                      <OverviewTable
                        clientID={clientId}
                        globalFilter={portfolioFilter}
                        nameHeader="Accounts"
                        valueHeader="POD Gains"
                        query={ACCOUNT_POD_GAINS_SERIES_PORTFOLIO}
                        onRowClick={() => {}}
                        onViewAllClick={() => {}}
                        portfolio
                      />
                    </Col>
                    <Col lg={12} xl={4}>
                      <OverviewTable
                        clientID={clientId}
                        globalFilter={portfolioFilter}
                        nameHeader="Accounts"
                        valueHeader="POD Losses"
                        query={ACCOUNT_POD_LOSSES_SERIES_PORTFOLIO}
                        onRowClick={() => {}}
                        onViewAllClick={() => {}}
                        portfolio
                      />
                    </Col>
                    <Col lg={12} xl={4}>
                      <OverviewTable
                        clientID={clientId}
                        globalFilter={portfolioFilter}
                        nameHeader="Account"
                        valueHeader="Opportunities"
                        query={ACCOUNT_POD_OPPORTUNITIES_SERIES_PORTFOLIO}
                        onRowClick={() => {}}
                        onViewAllClick={() => {}}
                        portfolio
                      />
                    </Col>
                  </Row>
                ),
              },
            }}
          </Widget>
        </Col>
      </Row>
      {tablesState.sharedState.filtering.filters.length > 0 && (
        <Row className="mt-3">
          <Col>
            <TableFilterTiles filtering={tablesState.sharedState.filtering} />
          </Col>
        </Row>
      )}
      <Row className="mt-3">
        <Col>
          <Widget>
            {{
              header: {
                content: {
                  left: (
                    <TableTabs
                      selectedTab={selectedTab}
                      tabs={[
                        {
                          name: BRANDS_TYPE,
                          total:
                            data && data.brandResumePortfolio
                              ? data.brandResumePortfolio.page.totalItems
                              : 0,
                        },
                      ]}
                    />
                  ),
                  center: (
                    <SearchInput
                      style={{ width: '300px' }}
                      placeholder="Search..."
                      onChange={(value) => {
                        tablesState.sharedState.searching.search(value);
                      }}
                    />
                  ),
                },
              },
              body: {
                content: (
                  <BrandsTable
                    error={error}
                    queryState={tablesState.queryState('brands')}
                    rows={data && data.brandResumePortfolio ? data.brandResumePortfolio.data : []}
                    showLoading={loading}
                    totalRows={
                      data && data.brandResumePortfolio
                        ? data.brandResumePortfolio.page.totalItems
                        : 0
                    }
                    onRowClick={(row) => handleBrandSelect(row.name)}
                  />
                ),
              },
            }}
          </Widget>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default Portfolio;
